import { createReducer, on } from '@ngrx/store';

import { RewardTokensActions } from './actions';
import { IRewardToken } from './reward-tokens.model';
import { IRewardTokenEligibility, IRewardTokensState, defaultRewardTokensState } from './state';

export const rewardTokensReducer = createReducer(
    defaultRewardTokensState,
    on(RewardTokensActions.setTokens, (state, { payload: tokens }): IRewardTokensState => {
        const tokensState: Record<string, IRewardToken> = {};

        for (const token of tokens) {
            tokensState[token.userTokenId] = token;
        }

        return {
            ...state,
            tokens: tokensState,
        };
    }),
    on(RewardTokensActions.setToken, (state, action): IRewardTokensState => {
        const tokensState = { ...state.tokens };

        const token = action.payload;
        tokensState[token.userTokenId] = token;

        return {
            ...state,
            tokens: tokensState,
        };
    }),
    on(RewardTokensActions.addTokens, (state, { payload: tokens }): IRewardTokensState => {
        const tokensState = { ...state.tokens };

        for (const token of tokens) {
            tokensState[token.userTokenId] = token;
        }

        return {
            ...state,
            tokens: tokensState,
        };
    }),
    on(RewardTokensActions.removeTokensByType, (state, action): IRewardTokensState => {
        const tokensState = { ...state.tokens };

        const toRemove = Object.values(tokensState).filter((t) => t.rewardTokenType === action.payload);

        toRemove.forEach((token) => {
            delete tokensState[token.userTokenId];
        });

        return {
            ...state,
            tokens: tokensState,
        };
    }),
    on(RewardTokensActions.removeTokensById, (state, action): IRewardTokensState => {
        const tokens = { ...state.tokens };

        for (const removedTokenId of action.userTokenIds) {
            delete tokens[removedTokenId];
        }

        return {
            ...state,
            tokens,
        };
    }),
    on(RewardTokensActions.setComboBetEligibility, (state, action): IRewardTokensState => {
        return {
            ...state,
            eligibilityState: { ...state.eligibilityState, ...{ comboBet: action.payload } },
        };
    }),
    on(RewardTokensActions.setTeaserBetEligibility, (state, action): IRewardTokensState => {
        return {
            ...state,
            eligibilityState: { ...state.eligibilityState, ...{ teaserBet: action.payload } },
        };
    }),
    on(RewardTokensActions.setSingleBetEligibilies, (state, { payload: eligibilities }): IRewardTokensState => {
        const singleBetState: Record<string, IRewardTokenEligibility[]> = {};

        for (const eligbility of eligibilities) {
            singleBetState[eligbility.pickId] = eligbility.eligibility;
        }

        return {
            ...state,
            eligibilityState: {
                ...state.eligibilityState,
                singleBet: singleBetState,
            },
        };
    }),
    on(RewardTokensActions.setBetBuilderEligibilities, (state, { payload: eligibilities }): IRewardTokensState => {
        const beBuilderState: Record<string, IRewardTokenEligibility[]> = {};

        for (const eligbility of eligibilities) {
            beBuilderState[eligbility.pickId] = eligbility.eligibility;
        }

        return {
            ...state,
            eligibilityState: {
                ...state.eligibilityState,
                betBuilder: beBuilderState,
            },
        };
    }),
    on(RewardTokensActions.showOnboarding, (state, _): IRewardTokensState => {
        return {
            ...state,
            onboardingState: {
                ...state.onboardingState,
                visible: true,
            },
        };
    }),
    on(RewardTokensActions.hideOnboarding, (state, _): IRewardTokensState => {
        return {
            ...state,
            onboardingState: {
                ...state.onboardingState,
                visible: false,
            },
        };
    }),
    on(RewardTokensActions.hideAcquisitionOnboarding, (state, _): IRewardTokensState => {
        return {
            ...state,
            onboardingState: {
                ...state.onboardingState,
                acquisitionHidden: true,
            },
        };
    }),
    on(RewardTokensActions.clearState, (state, _): IRewardTokensState => {
        return {
            ...state,
            tokens: {},
            eligibilityState: {
                singleBet: {},
                comboBet: [],
                teaserBet: [],
                betBuilder: {},
            },
            betstationTokens: {
                bogTokenState: {
                    selectedTokenId: null,
                    eligibility: null,
                },
                betStationFreeBet: {
                    selectedTokenId: null,
                    eligibility: null,
                },
                edsState: {
                    eligibilities: [],
                    selectedTokenIds: [],
                },
            },
            tokenRecoveryState: [],
        };
    }),
    on(RewardTokensActions.setBogEligibility, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                bogTokenState: {
                    ...state.betstationTokens.bogTokenState,
                    eligibility: action.payload,
                },
            },
        };
    }),
    on(RewardTokensActions.selectBogToken, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                bogTokenState: {
                    ...state.betstationTokens.bogTokenState,
                    selectedTokenId: action.payload.tokenId,
                },
            },
        };
    }),
    on(RewardTokensActions.setBetStationFreeBetEligibility, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                betStationFreeBet: {
                    ...state.betstationTokens.betStationFreeBet,
                    eligibility: action.payload,
                },
            },
        };
    }),
    on(RewardTokensActions.selectBetStationFreeBetToken, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                betStationFreeBet: {
                    ...state.betstationTokens.betStationFreeBet,
                    selectedTokenId: action.payload.tokenId,
                },
            },
        };
    }),
    on(RewardTokensActions.setEdsEligibility, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                edsState: {
                    ...state.betstationTokens.edsState,
                    eligibilities: action.payload,
                },
            },
        };
    }),
    on(RewardTokensActions.selectEdsTokens, (state, action): IRewardTokensState => {
        return {
            ...state,
            betstationTokens: {
                ...state.betstationTokens,
                edsState: {
                    ...state.betstationTokens.edsState,
                    selectedTokenIds: action.payload.tokenIds,
                },
            },
        };
    }),

    on(RewardTokensActions.storeRecoverableTokens, (state, action): IRewardTokensState => {
        return {
            ...state,
            tokenRecoveryState: [...state.tokenRecoveryState, ...action.payload],
        };
    }),
    on(RewardTokensActions.clearRecoverableTokens, (state): IRewardTokensState => {
        return {
            ...state,
            tokenRecoveryState: [],
        };
    }),
    on(RewardTokensActions.removeRecoverableTokenForContext, (state, action): IRewardTokensState => {
        return {
            ...state,
            tokenRecoveryState: state.tokenRecoveryState.filter(
                (t) => t.context.betslipType !== action.betslipType || t.context.pickId?.toString() !== action.pickId?.toString(),
            ),
        };
    }),
);
